import { DateTime } from 'luxon';
import { DateRangeInput } from '@/features/events/components/InlineFields/DateTime/components/DateRangeInput';
import { useExperienceEvents } from '@/features/events/hooks/useExperienceEvents';
import {
  getEventDateBounds,
  intersectWindows,
  isEmptyWindow,
  normalizeEventListDisplay,
  resolveEventListWindow,
  resolveScheduleDatesWindow,
  toEventListBound,
  toEventListDay,
} from '@/features/events/lib/eventListWindow.lib';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useUpdateExperience } from '@/features/experiences/hooks/useUpdateExperience';
import { toTemplateLayout } from '@/features/printables/printableConfigAdapter';
import { getTemplateMeta } from '@/features/printables/templates';
import { EventListDisplay } from '@/types/gql.generated';
import { Box, Center, Spinner, Text } from '@/ui';
import { RadioRow, SidebarCard, SidebarSection } from '../SidebarKit';
import { useCustomizeControl, type ValueBinding } from '../useCustomizeControl';

type Props = ValueBinding<EventListDisplay> & {
  eventListDisplayOptions: EventListDisplay[];
};

const OPTION_CONFIGS: {
  value: EventListDisplay;
  /** Null where the layout names the option (see fullLabel below). */
  label: string | null;
  sublabel: string;
}[] = [
  {
    value: EventListDisplay.All,
    label: 'All events',
    sublabel: 'Everything on this schedule',
  },
  {
    value: EventListDisplay.Upcoming,
    label: 'Upcoming only',
    sublabel: 'From today forward',
  },
  {
    value: EventListDisplay.Full,
    label: null,
    sublabel: 'Every month, including the empty ones',
  },
  {
    value: EventListDisplay.Custom,
    label: 'Date range',
    sublabel: 'A fixed window you choose',
  },
];

export const WhatToShowSection = ({
  eventListDisplayOptions,
  value,
  onChange,
  onPreview,
}: Props) => {
  // The mode arrives through the binding; the two dates aren't a previewable
  // enum, so the inputs below read and write them directly. savedFor('child')
  // rather than the context experience, so both sides read the same copy — a
  // schedule that is its own view previews itself.
  const { displayTimezone, isPublishedView, parentExperience } =
    usePageExperienceContext();
  const { savedFor } = useCustomizeControl();
  const savedExperience = savedFor('child');
  const { setEventListDateRange } = useUpdateExperience();

  const normalized = normalizeEventListDisplay(value);
  // Only the metas know what whole period a layout fills, so FULL takes its name
  // from the active layout ("Full year" on the month-card grids). A layout that
  // offers the mode without naming it can't be shown, and dropping it here is
  // better than a blank row.
  const { eventListDisplayFullLabel: fullLabel } = getTemplateMeta(
    toTemplateLayout(savedExperience.layoutId)
  );
  const visibleOptions = OPTION_CONFIGS.flatMap((option) => {
    if (!eventListDisplayOptions.includes(option.value)) {
      return [];
    }
    const label = option.label ?? fullLabel;
    return label ? [{ ...option, label }] : [];
  });
  // Saved mode, not hovered: sliding date inputs in under the cursor would move
  // the row the pointer is on.
  const isCustom = normalized === EventListDisplay.Custom;

  const scheduleWindow = parentExperience
    ? resolveScheduleDatesWindow(parentExperience, displayTimezone)
    : {};
  // The page's shared event list is bounded to the active pager period (and its
  // facet filters), so it cannot answer where the events actually start and end.
  // Fetch the
  // owning schedule without those view filters while the inputs are open. Keep
  // only Schedule dates as the outer cap; for a standalone view, explicitly
  // clear its own Custom dates so the query can see beyond the window being
  // edited.
  const { events: windowDefaultEvents, isLoading: isLoadingBounds } =
    useExperienceEvents({
      experience: isCustom ? (parentExperience ?? savedExperience) : undefined,
      filters: {
        startDate: parentExperience
          ? (scheduleWindow.start?.toISODate() ?? undefined)
          : undefined,
        endDate: parentExperience
          ? (scheduleWindow.end?.toISODate() ?? undefined)
          : undefined,
      },
      published: isPublishedView,
    });

  // An unset bound means unbounded on that side, so the input shows the first /
  // last event as a placeholder rather than a value the author never picked.
  const window = resolveEventListWindow(savedExperience, displayTimezone);
  const { start, end } = window;
  const eventBounds = getEventDateBounds(windowDefaultEvents);
  const today = DateTime.local({ zone: displayTimezone }).startOf('day');
  const fallbackStartDate = eventBounds.start ?? today;
  const fallbackEndDate = eventBounds.end ?? today;
  // An empty result and a pending one are both [] here.
  const isAwaitingBounds = isLoadingBounds && (!start || !end);

  // With no overlap the view renders the schedule's periods, all empty (see
  // resolveViewWindow). Right on the page, a mystery in the panel, where the
  // range still reads exactly as typed — so say so beside the inputs.
  const isOutsideSchedule =
    isCustom && isEmptyWindow(intersectWindows(window, scheduleWindow));
  const scheduleSpan = [scheduleWindow.start, scheduleWindow.end]
    .map((bound) => bound?.toLocaleString(DateTime.DATE_MED) ?? 'open')
    .join(' – ');

  const handleSelect = (display: EventListDisplay) => {
    // RadioRow fires onClick even when already selected, and the commit behind
    // onChange clears the dates, so re-selecting has to be inert.
    if (display === normalized) {
      return;
    }
    onChange(display);
  };

  // Clamps compare UTC-anchored days, the frame the bounds are stored in. The
  // picker emits browser-local midnight while start/end resolve in
  // displayTimezone, so an unnormalized comparison fires spuriously when those
  // differ. Harmless today only because the clamp rewrites the day it read.
  const handleStartChange = (newStart: DateTime) => {
    const nextStart = toEventListDay(newStart);
    const isAfterEnd = Boolean(end && nextStart > toEventListDay(end));
    setEventListDateRange(
      toEventListBound(nextStart),
      isAfterEnd ? toEventListBound(nextStart) : toEventListBound(end)
    );
  };

  const handleEndChange = (newEnd: DateTime) => {
    const nextEnd = toEventListDay(newEnd);
    const isBeforeStart = Boolean(start && nextEnd < toEventListDay(start));
    setEventListDateRange(
      isBeforeStart ? toEventListBound(nextEnd) : toEventListBound(start),
      toEventListBound(nextEnd)
    );
  };

  // No choice to offer if only one option is available.
  if (visibleOptions.length <= 1) return null;

  return (
    <SidebarSection label="What to show">
      {/* The date picker's popper is positioned inline, so the card can't clip
          its overflow while the range inputs are showing. */}
      <SidebarCard overflow={isCustom ? 'visible' : 'hidden'}>
        {visibleOptions.map((option) => (
          <RadioRow
            isActive={normalized === option.value}
            key={option.value}
            label={option.label}
            sublabel={option.sublabel}
            onClick={() => handleSelect(option.value)}
            onMouseEnter={() => onPreview(option.value)}
            onMouseLeave={() => onPreview(null)}
          />
        ))}
        {isCustom && (
          <Box px={3.5} py={3}>
            {isAwaitingBounds ? (
              <Center py={2}>
                <Spinner color="ink.muted" size="sm" />
              </Center>
            ) : (
              <DateRangeInput
                endDate={end ?? fallbackEndDate}
                endPlaceholder="No end"
                fallbackEndDate={fallbackEndDate}
                fallbackStartDate={fallbackStartDate}
                hasRecurrence={false}
                isAllDay={false}
                isFluid
                showEndPlaceholder={!end}
                showStartPlaceholder={!start}
                startDate={start ?? fallbackStartDate}
                startPlaceholder="No start"
                untilDate={null}
                onEndChange={handleEndChange}
                onStartChange={handleStartChange}
                onUntilChange={() => {}}
              />
            )}
            {isOutsideSchedule && (
              <Text color="ink.muted" fontSize="ah-status" mt={2}>
                This range falls outside the schedule&rsquo;s dates (
                {scheduleSpan}), so this view will be empty.
              </Text>
            )}
          </Box>
        )}
      </SidebarCard>
    </SidebarSection>
  );
};
